import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, AlertTriangle, HardDrive, CheckCircle2 } from 'lucide-react';
import { API, useAuth } from '../App';
import { istTodayStr } from '../lib/ist';

const SOURCES = [
  { id: 'booths', label: 'Booth diagnostics (events, crashes, activity)' },
  { id: 'payments', label: 'Payments (orders, refunds, anomalies, settlements)' },
  { id: 'admin', label: 'Admin audit log' },
  { id: 'alerts', label: 'Alert history (email/SMS)' },
  { id: 'prints', label: 'Print logs' },
  { id: 'vouchers', label: 'Voucher redemptions' },
];

export default function LogsCenter() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [startD, setStartD] = useState(istTodayStr(-30));
  const [endD, setEndD] = useState(istTodayStr());
  const [kiosks, setKiosks] = useState([]);
  const [selectedKiosks, setSelectedKiosks] = useState([]);
  const [sources, setSources] = useState(SOURCES.map((s) => s.id));
  const [coverage, setCoverage] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [k, c] = await Promise.all([
        axios.get(`${API}/kiosks`, { headers }),
        axios.get(`${API}/logs/coverage`, { headers }),
      ]);
      setKiosks(k.data || []);
      setCoverage(c.data);
    } catch (e) { /* silent */ }
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const toggle = (arr, setArr, id) =>
    setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const download = async () => {
    if (!sources.length) { toast.error('Pick at least one log source'); return; }
    setDownloading(true);
    try {
      const params = { start: startD, end: endD, sources: sources.join(',') };
      if (selectedKiosks.length) params.kiosk_ids = selectedKiosks.join(',');
      const r = await axios.get(`${API}/logs/export`, { headers, params, responseType: 'blob', timeout: 300000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `piczoo-logs_${startD}_to_${endD}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Log archive downloaded');
    } catch (e) {
      toast.error('Export failed — try a shorter date range');
    }
    setDownloading(false);
  };

  const pullNeeded = (coverage?.booths || []).filter((b) => b.needs_local_pull);
  const fresh = (coverage?.booths || []).filter((b) => !b.needs_local_pull);

  return (
    <div className="space-y-6" data-testid="logs-center-page">
      <div>
        <h1 className="font-serif text-3xl text-charcoal-900">Logs Center</h1>
        <p className="text-charcoal-800/60 text-sm mt-1">
          Consolidated 365-day archive — booth diagnostics, payments, admin actions, alerts, prints and vouchers in one compartmentalised ZIP. All timestamps in IST.
        </p>
      </div>

      {/* Coverage warnings */}
      {pullNeeded.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="coverage-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Some booths have logs still on the machine</p>
              <p className="text-xs text-amber-800/80 mt-1">
                These booths are offline and haven't uploaded logs in over 48 hours. Anything since their last upload only exists on the booth — press <strong>Ctrl+Shift+L</strong> on the booth to export the on-machine bundle.
              </p>
              <ul className="mt-2 space-y-1">
                {pullNeeded.map((b) => (
                  <li key={b.kiosk_id} className="text-xs text-amber-900" data-testid={`pull-needed-${b.kiosk_id}`}>
                    <HardDrive className="w-3 h-3 inline mr-1" />
                    <strong>{b.name}</strong> — last uploaded log: {b.last_log_at_ist || 'never'}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      {coverage && pullNeeded.length === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2" data-testid="coverage-ok">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <p className="text-sm text-emerald-900">All booth logs are fully uploaded — the archive below is complete.</p>
        </div>
      )}

      <div className="bg-white border border-charcoal-900/10 rounded-xl p-6 space-y-5">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-charcoal-800/60 mb-1">From (IST)</label>
            <input type="date" value={startD} onChange={(e) => setStartD(e.target.value)}
              className="px-3 py-2 border border-charcoal-900/10 rounded-lg text-sm" data-testid="logs-start-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-charcoal-800/60 mb-1">To (IST)</label>
            <input type="date" value={endD} onChange={(e) => setEndD(e.target.value)}
              className="px-3 py-2 border border-charcoal-900/10 rounded-lg text-sm" data-testid="logs-end-input" />
          </div>
          <div className="flex gap-2">
            {[['7d', 7], ['30d', 30], ['90d', 90], ['365d', 365]].map(([label, days]) => (
              <button key={label} onClick={() => { setStartD(istTodayStr(-days)); setEndD(istTodayStr()); }}
                className="px-3 py-2 text-xs font-medium border border-charcoal-900/10 rounded-lg hover:bg-cream-50" data-testid={`logs-preset-${label}`}>
                Last {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-charcoal-800/60 mb-2">Log sources</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {SOURCES.map((s) => (
              <label key={s.id} className="flex items-center gap-2 cursor-pointer text-sm text-charcoal-900">
                <input type="checkbox" checked={sources.includes(s.id)} onChange={() => toggle(sources, setSources, s.id)}
                  className="accent-charcoal-900" data-testid={`logs-source-${s.id}`} />
                {s.label}
              </label>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-charcoal-800/60 mb-2">Booths (none selected = all booths)</p>
          <div className="flex flex-wrap gap-2">
            {kiosks.map((k) => (
              <button key={k.id} onClick={() => toggle(selectedKiosks, setSelectedKiosks, k.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${selectedKiosks.includes(k.id) ? 'bg-charcoal-900 text-white border-charcoal-900' : 'bg-white text-charcoal-800/70 border-charcoal-900/10'}`}
                data-testid={`logs-booth-${k.id}`}>
                {k.name}
              </button>
            ))}
          </div>
        </div>

        <button onClick={download} disabled={downloading}
          className="flex items-center gap-2 bg-charcoal-900 text-cream-50 px-5 py-2.5 rounded-lg hover:bg-charcoal-800 disabled:opacity-50"
          data-testid="logs-download-btn">
          <Download className="w-4 h-4" />
          {downloading ? 'Building archive…' : 'Download log archive (ZIP)'}
        </button>
        <p className="text-[11px] text-charcoal-800/40">
          ZIP contains: booths/&lt;name&gt;-diagnostics.jsonl · payments/orders.csv, refunds.csv, anomalies.jsonl, settlements.csv · admin/audit-log.csv · alerts/alerts.csv · prints/print-logs.csv · vouchers/redemptions.csv · manifest.json
        </p>
      </div>
    </div>
  );
}
